<template>
  <v-app>
    <environment-banner />
    <router-view />
    <v-btn
      elevation="0"
      class="btn-scroll-top"
      v-scroll="onScroll"
      v-show="fab"
      fab
      white
      fixed
      bottom
      right
      @click="toTop"
    >
      <v-icon>mdi-chevron-up</v-icon>
    </v-btn>
  </v-app>
</template>

<script>
import EnvironmentBanner from '@/components/Alert/EnvironmentBanner'

// Heartbeat interval for keeping a 仮予約 (temporary booking) alive while the customer is active.
// Derived from the backend hold time: expiry / 2 - 1 min = 15 / 2 - 1 = 6.5 min. The -1 min is a
// buffer so a slow browser / network does not let the hold lapse. Keep in sync with the backend
// AUTO_DELETE_TEMPORARY_BOOKING app_constant.
const BOOKING_EXTEND_INTERVAL_MS = 6.5 * 60 * 1000
const BOOKING_ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart']

export default {
  name: 'App',
  components: { EnvironmentBanner },
  data () {
    return {
      fab: false,
      lastBookingActivityAt: 0,
      bookingExtendIntervalId: null
    }
  },
  computed: {
    temporaryBookingGroupId () {
      return this.$store.getters.temporaryBookingGroupId
    }
  },
  watch: {
    temporaryBookingGroupId: {
      immediate: true,
      handler (id) {
        if (id) this.startBookingExtendHeartbeat()
        else this.stopBookingExtendHeartbeat()
      }
    }
  },
  methods: {
    onScroll (e) {
      if (typeof window === 'undefined') return
      const top = window.pageYOffset || e.target.scrollTop || 0
      this.fab = top > 20
    },
    toTop () {
      this.$vuetify.goTo(0)
    },
    checkLastAction () {
      const lastAction = localStorage.getItem('lastAction') ? Number(localStorage.getItem('lastAction')) : null
      if (this.$store.getters.isLoggedIn) {
        if (lastAction) {
          const secondsSince = (Date.now() - lastAction) / 1000
          if (secondsSince > 3600) {
            let reason = ''
            if (sessionStorage.getItem('lastAction')) {
              // user has interacted with this tab at least once, so
              reason = 'inactive'
            }
            this.$router.push({ name: 'logout', query: { reason } })
          }
        }
      }
    },
    onBookingActivity () {
      this.lastBookingActivityAt = Date.now()
    },
    startBookingExtendHeartbeat () {
      this.stopBookingExtendHeartbeat()
      this.lastBookingActivityAt = Date.now()
      this.bookingExtendIntervalId = setInterval(this.runBookingExtendHeartbeat, BOOKING_EXTEND_INTERVAL_MS)
    },
    stopBookingExtendHeartbeat () {
      if (this.bookingExtendIntervalId) {
        clearInterval(this.bookingExtendIntervalId)
        this.bookingExtendIntervalId = null
      }
    },
    runBookingExtendHeartbeat () {
      if (!this.temporaryBookingGroupId) return
      // Only extend when the customer interacted within the last interval window. This keeps a
      // 仮予約 alive without spamming the API on every keystroke.
      if (Date.now() - this.lastBookingActivityAt <= BOOKING_EXTEND_INTERVAL_MS) {
        // Background heartbeat: no loading overlay / error dialog. An expired hold is handled by
        // the final-confirmation stock check.
        this.$store.dispatch('extendTemporaryBookingTimeout').catch(() => {})
      }
    }
  },
  mounted () {
    if (process.env.NODE_ENV === 'development') {
      // this.$store.commit('setToken', 'eyJhbGciOiJSUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6MSwicm9sZSI6Ik1BSU4iLCJjbGllbnRJZCI6MSwic3ViTWVtYmVySWQiOm51bGwsInR5cGUiOjEsImlhdCI6MTYzMTU5ODgwMiwiZXhwIjoxNjYzMTU2NDAyfQ.qjHK05bLLBXZmRrSJfqUr9gqlmK2NcFjl49IbNYS4ItUwKJRpY2BcCZAcgyZnfghCcCgdYjzD_OpPCtCOA38oIaiFaPRmFvenaI2ewI1OZ977Kt2Timhe-caDzFOGTFLYd3JCLDsndlWQnjVMzq2jZO6L7P7RoAAVuIrcPiMyXx6WaLde9JLX7bcY0Z7Hbb-fBGzCQ-VNtZrPm34yOGnHMYRJo_djNRa0_vieHVaEx6TFIk_tiLVpIF2ZZGzicI6CO82YBZpPTnSUbTmw0Hirvt5Rst1lH3xea6NguqNEp3cKQ1YZ6LBE6qckIuI5qLai1TZC2ZnOTMAKyJ0Uf6Mbg')
    }

    ['click', 'scroll', 'keydown', 'touchstart'].forEach(event => {
      document.addEventListener(event, () => {
        this.$store.dispatch('logLastAction')
      })
    })
    setInterval(() => {
      this.checkLastAction()
    }, 1000)
    this.checkLastAction()

    // Track mouse/keyboard activity (in memory only) to drive the 仮予約 extend heartbeat.
    // Not folded into logLastAction above: mousemove must not affect the 1-hour auto-logout
    // and must not write to localStorage on every move.
    BOOKING_ACTIVITY_EVENTS.forEach(event => {
      document.addEventListener(event, this.onBookingActivity)
    })
  },
  beforeDestroy () {
    this.stopBookingExtendHeartbeat()
    BOOKING_ACTIVITY_EVENTS.forEach(event => {
      document.removeEventListener(event, this.onBookingActivity)
    })
  }
}
</script>

<style lang="scss" scoped>
@use 'src/assets/styles/modules/variables-scss' as v;

::v-deep {
  .v-btn.btn-scroll-top {
    color: var(--color__primary) !important;
    z-index: 3 !important;
    @media (min-width: v.$max-width-screen) {
      color: var(--color__white) !important;
      background-color: var(--color__primary) !important;
    }
  }
}
</style>
